import { Head, Link, router } from "@inertiajs/react"
import { useEffect, useState } from "react"
import { Download, FileText, Loader2 } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@/components/ui/empty"
import { Input } from "@/components/ui/input"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import AppLayout from "@/layouts/app-layout"
import { brokerBrochuresPath } from "@/routes"
import type { BreadcrumbItem, Brochure } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Brochures", href: brokerBrochuresPath() },
]

interface Props {
  brochures: Brochure[]
}

const statusBadge: Record<Brochure["status"], { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  pending: { label: "Queued", variant: "secondary" },
  processing: { label: "Generating", variant: "secondary" },
  completed: { label: "Ready", variant: "default" },
  failed: { label: "Failed", variant: "destructive" },
}

export default function Index({ brochures }: Props) {
  const [query, setQuery] = useState("")

  const pending = brochures.some((b) => b.status === "pending" || b.status === "processing")
  useEffect(() => {
    if (!pending) return
    const id = setInterval(() => {
      router.reload({ only: ["brochures"] })
    }, 3000)
    return () => clearInterval(id)
  }, [pending])

  const filtered = brochures.filter((b) =>
    [b.property_name, b.suite_number, b.label]
      .join(" ")
      .toLowerCase()
      .includes(query.toLowerCase())
  )

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Brochures" />

      <div className="space-y-6 p-6">
        <div>
          <h1 className="text-2xl font-semibold">Brochures</h1>
          <p className="text-muted-foreground text-sm">
            All generated brochures. Download a PDF or jump to its suite.
          </p>
        </div>

        <Input
          placeholder="Search by property, suite, or type..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="max-w-sm"
        />

        {filtered.length === 0 ? (
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <FileText />
              </EmptyMedia>
              <EmptyTitle>No brochures yet</EmptyTitle>
              <EmptyDescription>
                Open a suite and click “Generate Brochure” to create one.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Property</TableHead>
                  <TableHead>Suite</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Download</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((b) => {
                  const status = statusBadge[b.status]
                  return (
                    <TableRow key={b.id}>
                      <TableCell className="font-medium">
                        <Link
                          href={`/broker/properties/${b.property_id}/suites/${b.suite_id}`}
                          className="hover:underline"
                        >
                          {b.property_name}
                        </Link>
                      </TableCell>
                      <TableCell>{b.suite_number}</TableCell>
                      <TableCell>
                        {b.label}
                        {b.branded && (
                          <Badge variant="outline" className="ml-2">Branded</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={status.variant}>
                          {(b.status === "pending" || b.status === "processing") && (
                            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                          )}
                          {status.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {new Date(b.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right">
                        {b.ready && b.download_url ? (
                          <Button
                            variant="outline"
                            size="sm"
                            render={<a href={b.download_url} target="_blank" rel="noreferrer" />}
                          >
                            <Download className="mr-2 h-4 w-4" />
                            PDF
                          </Button>
                        ) : (
                          <span className="text-muted-foreground text-sm">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </AppLayout>
  )
}
